import { Box, Button, ClickAwayListener, Paper, Popper, type SxProps, Typography } from '@mui/material';
import { theme } from '../../theme';
import { type AriaAttributes, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { OptionsList } from './OptionsList';
export type MultiSelectOption = {
  label: string;
  count: number;
};
type MultiSelectDropdownBaseProps<TFilterOptions extends string> = {
  placeholder?: string;
  options: {
    label: TFilterOptions;
    count: number;
  }[];
  selectedOptions: TFilterOptions[];
  onChange: (selected: TFilterOptions[]) => void;
  forceClose?: boolean;
};
type MultiSelectDropdownLabellingProps = {
  label: string;
  labelledById?: string;
} | {
  label?: string;
  labelledById: string;
};
type Props<TFilterOptions extends string> = MultiSelectDropdownBaseProps<TFilterOptions> & MultiSelectDropdownLabellingProps;
const styles = {
  button: {
    justifyContent: 'space-between',
    textAlign: 'left',
    borderRadius: '64px',
    py: 1,
    minHeight: '40px',
    userSelect: 'none',
    textTransform: 'none',
    '&:focus-visible': {
      outline: '2px solid',
      outlineColor: 'blues.light',
      cursor: 'pointer'
    },
    transition: 'none'
  },
  buttonTextContainer: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    gap: 0.5
  },
  buttonLabel: {
    fontSize: '14px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  selectionCountSuffix: {
    fontSize: '14px',
    color: 'primary.main',
    flexShrink: 0
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ml: 2,
    color: 'neutral.dark',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.short
    })
  },
  listboxContainer: {
    minWidth: '225px',
    borderRadius: '8px',
    py: 2,
    my: 2,
    gap: 2
  },
  listboxLabelContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    py: 2,
    px: 3
  },
  listboxLabelText: {
    fontSize: '14px',
    color: 'neutral.dark'
  }
} as const satisfies Record<string, SxProps>;
export const MultiSelectDropdown = <TFilterOptions extends string,>({
  label,
  labelledById,
  options,
  placeholder,
  selectedOptions,
  onChange,
  forceClose
}: Props<TFilterOptions>) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const uniqueId = useId();
  const optionsId = `dropdown-options-${uniqueId}`;
  useEffect(() => {
    if (forceClose && isOpen) {
      setIsOpen(false);
    }
  }, [forceClose, isOpen]);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const hasSelections = !!selectedOptions.length;
  const updateSelection = useCallback((option: TFilterOptions) => {
    const isSelected = selectedOptions.includes(option);
    const newSelected = isSelected ? selectedOptions.filter((item: TFilterOptions) => item !== option) : [...selectedOptions, option];
    onChange(newSelected);
  }, [onChange, selectedOptions]);
  const labelText = selectedOptions.length > 0 ? selectedOptions[0] : placeholder || label || '';
  const ariaProps: AriaAttributes = {};
  if (labelledById) {
    ariaProps['aria-labelledby'] = labelledById;
  } else if (label !== undefined) {
    ariaProps['aria-label'] = label;
  }
  const handleToggleOpen = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);
  const selectedOptionsSet = useMemo<Set<TFilterOptions>>(() => new Set(selectedOptions), [selectedOptions]);
  return <Box ref={dropdownRef}>
      <Button ref={buttonRef} onClick={handleToggleOpen} aria-haspopup="listbox" aria-expanded={isOpen} aria-controls={optionsId} role="combobox" variant="outlined" fullWidth sx={{
      ...styles.button,
      backgroundColor: hasSelections ? 'greens.light' : 'white',
      borderWidth: hasSelections ? '2px' : '1px',
      borderColor: hasSelections ? 'primary.main' : 'neutral.lighter'
    }} {...ariaProps}>
        <Box component="span" sx={styles.buttonTextContainer}>
          <Typography variant="textBold" component="span" sx={{
          ...styles.buttonLabel,
          color: hasSelections ? 'primary.main' : 'neutral.dark'
        }}>
            {labelText}
          </Typography>
          {hasSelections && selectedOptions.length > 1 && <Typography variant="textBold" component="span" sx={styles.selectionCountSuffix}>
              +{selectedOptions.length - 1}
            </Typography>}
        </Box>
        <Box component="span" sx={{
        ...styles.iconContainer,
        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
      }}>
          <Box component="span" className="mask-icon mask-icon-expand-more" aria-hidden="true" sx={{
          width: '24px',
          height: '24px',
          backgroundColor: hasSelections ? 'primary.main' : 'neutral.dark'
        }} />
        </Box>
      </Button>

      <Popper open={isOpen} anchorEl={buttonRef.current} disablePortal placement="bottom-start" popperOptions={{
      strategy: 'fixed'
    }} sx={{
      zIndex: 200
    }}>
        <ClickAwayListener onClickAway={() => {
        setIsOpen(false);
        buttonRef.current?.focus();
      }}>
          <Paper id={optionsId} role="listbox" aria-multiselectable="true" tabIndex={-1} elevation={4} sx={styles.listboxContainer} {...ariaProps}>
            {!labelledById && <Box aria-hidden="true" sx={styles.listboxLabelContainer}>
                <Typography variant="textBold" sx={styles.listboxLabelText}>
                  {label}
                </Typography>
              </Box>}

            <OptionsList options={options} selectedOptionsSet={selectedOptionsSet} onOptionSelect={updateSelection} onClose={() => {
            setIsOpen(false);
            buttonRef.current?.focus();
          }} />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>;
};